import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useFeatureFlags } from 'src/context/FeatureFlagsContext';
import routePaths from 'src/contstant/routePaths';

/** Where users are sent when AuditWhiz is off — the Questions list.
 *  Sourced from the shared route table so this redirect stays in sync
 *  with the router if the path ever changes. */
const QUESTIONS_LIST_ROUTE = routePaths.QUESTION.path;

interface RequireAuditWhizProps {
  readonly children: ReactNode;
}

/**
 * Route-level gate for AuditWhiz pages (Generate, Review & Publish).
 *
 * AuditWhiz is gated behind the `enableAuditWhiz` flag (off in GAMMA/PROD
 * until launch). Hiding only the "Generate with Astra" entry point leaves the
 * pages reachable by direct URL, so every AuditWhiz route is wrapped in this
 * guard: when the flag is off it redirects to the Questions list instead of
 * rendering an unreleased screen. One shared guard for all AuditWhiz routes so
 * the flag check isn't duplicated per page.
 *
 * Rendered OUTSIDE V2Adapter in AppRouter so the Stencil page never mounts
 * when the flag is off; `useFeatureFlags` resolves against the provider at the
 * app root.
 */
export function RequireAuditWhiz({ children }: RequireAuditWhizProps): JSX.Element {
  const { enableAuditWhiz } = useFeatureFlags();

  if (!enableAuditWhiz) {
    return <Navigate to={QUESTIONS_LIST_ROUTE} replace />;
  }

  return <>{children}</>;
}
